/**
 * The main People list (one row per person across every group and Tutoring), from /api/people.
 * The server works out the numbers with the same ledger engine every group uses; this keeps a
 * saved copy so the page still opens offline.
 */
import {
  personOverview,
  shownCents,
  shownTotals,
  type PersonOverview,
  type PersonSource,
} from "@split/core";
import { api } from "./api.ts";

export interface PeopleLine {
  kind: "group" | "tutoring";
  /** Which part of their overall total (missing on a copy saved before 2026-10-23). */
  category?: "purchases" | "tutoring";
  groupId?: string;
  personId?: string;
  label: string;
  balanceMicro: number;
  paidMicro?: number;
  /** Tutor lines: pay earned, and what they bought from you. */
  earnedMicro?: number;
  boughtMicro?: number;
  role?: "student" | "tutor";
  record?: string;
  recordName?: string;
  /** Why this line is on the row (owner only): the links that joined it. */
  joinedBy?: { kind: LinkKind; detail: string }[];
  /** Links on it that were left out (they'd put you with someone else). */
  ignoredLinks?: { kind: LinkKind; detail: string }[];
}

export type LinkKind = "you" | "account" | "identity" | "tutor";

export interface PeopleRow {
  key: string;
  name: string;
  hasAccount: boolean;
  isYou: boolean;
  groups: string[];
  balanceMicro: number;
  lines: PeopleLine[];
  /** Each line as the shared card shows it (missing on an old saved copy). */
  sources?: PersonSource[];
  /** The overall total as shown: purchases, tutoring and both (missing on an old saved copy). */
  purchasesCents?: number;
  tutoringCents?: number;
  totalCents?: number;
  identityId?: string | null;
}

export interface Suggestion {
  records: [string, string];
  names: [string, string];
  rows: [string, string];
  reason: "full-name" | "first-name";
}

export interface IdentityAction {
  id: string;
  kind: string;
  summary: string;
  at: string;
  undone: boolean;
}

const KEY = "people:all";
/** How long after saved changes main People and the Overview load their totals again (ms). */
const REFRESH_MS = 400;

function saved(): { scope: "everyone" | "you"; people: PeopleRow[] } | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

class PeopleData {
  rows = $state<PeopleRow[] | null>(saved()?.people ?? null);
  scope = $state<"everyone" | "you">(saved()?.scope ?? "everyone");
  suggestions = $state<Suggestion[]>([]);
  actions = $state<IdentityAction[]>([]);
  busy = $state(false);
  stale = $state(false);
  error = $state("");

  async load() {
    try {
      const r = await api<{
        scope: "everyone" | "you";
        people: PeopleRow[];
        suggestions?: Suggestion[];
        actions?: IdentityAction[];
      }>("/api/people");
      this.rows = r.people;
      this.suggestions = r.suggestions ?? [];
      this.actions = r.actions ?? [];
      this.scope = r.scope;
      this.stale = false;
      this.error = "";
      try {
        localStorage.setItem(KEY, JSON.stringify(r));
      } catch {
        /* storage blocked: it just won't open offline */
      }
    } catch (e) {
      this.stale = true;
      if (!this.rows) this.error = (e as Error).message;
    }
  }

  private soon: ReturnType<typeof setTimeout> | null = null;
  /**
   * Load again shortly after a change was saved, so main People and the Overview follow what you
   * just did (a burst of changes is one load). Screens call it whenever saved changes move on.
   */
  refreshSoon(ms = REFRESH_MS) {
    if (this.soon) clearTimeout(this.soon);
    this.soon = setTimeout(() => {
      this.soon = null;
      void this.load();
    }, ms);
  }

  /** Owner only: merge, split, "not the same", undo. Each reloads the list. Returns an error or "". */
  async act(
    path: "merge" | "split" | "not-same" | "undo",
    body: Record<string, unknown>,
  ): Promise<string> {
    this.busy = true;
    try {
      await api(`/api/people/${path}`, { body });
      await this.load();
      return "";
    } catch (e) {
      return (e as Error).message;
    } finally {
      this.busy = false;
    }
  }

  clear() {
    this.rows = null;
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* fine */
    }
  }
}

export const people = new PeopleData();

/**
 * One person's overall view: every group and Tutoring netted into one total. The one place this
 * is worked out on the website, so main People and the Overview always agree (Miles, 2026-10-23).
 * A copy saved before 2026-10-23 has only lines (their balances, no detail).
 */
export function overviewOf(r: PeopleRow): PersonOverview {
  const sources: PersonSource[] =
    r.sources ??
    r.lines.map((l, i) => ({
      key: l.record ?? `line:${i}`,
      kind: l.kind,
      ...(l.role ? { role: l.role } : {}),
      category: l.category ?? (l.kind === "group" ? "purchases" : "tutoring"),
      label: l.label,
      ...(l.groupId ? { groupId: l.groupId } : {}),
      balanceCents: shownCents(l.balanceMicro),
      rows: [],
      entries: [],
      unpaid: [],
    }));
  return personOverview(sources);
}

/** A person's overall total in cents (positive: they owe you), the exact sum of their lines. */
export const rowCents = (r: PeopleRow): number => r.totalCents ?? overviewOf(r).totalCents;

/** Owed to you and you owe, across everyone: the exact sum of the People rows (you aren't counted). */
export const peopleTotals = (rows: readonly PeopleRow[]) =>
  shownTotals(rows.filter((r) => !r.isYou).map(rowCents));

/** Who owes you most and who you owe, one line per person with their overall total. */
export function peopleRanked(rows: readonly PeopleRow[]) {
  const list = rows.filter((r) => !r.isYou).map((r) => ({ label: r.name, cents: rowCents(r) }));
  const by = (a: { cents: number }, b: { cents: number }) => Math.abs(b.cents) - Math.abs(a.cents);
  return {
    owes: list.filter((x) => x.cents > 0).sort(by),
    owed: list.filter((x) => x.cents < 0).sort(by),
  };
}
